/**
 * API client for the Production Insights feature
 */

import { InsightCategory, Insight, InsightCategoryData } from '../types/insights';

// REACT_APP_API_URL is typically '/api' (proxied) or full URL for direct access
const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

interface ApiResponse<T> {
  success: boolean;
  data: T | null;
  error?: string;
  timestamp: string;
}

/**
 * Get the current user's email from localStorage
 */
const getUserEmail = (): string => {
  const email = localStorage.getItem('user_email');
  return email || 'anonymous@filmiq.ai';
};

/**
 * Make an authenticated API request
 */
const fetchWithAuth = async <T>(endpoint: string): Promise<ApiResponse<T>> => {
  const userEmail = getUserEmail();
  
  const response = await fetch(`${API_BASE_URL}${endpoint}`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'X-User-Email': userEmail,
    },
  });
  
  if (!response.ok) {
    throw new Error(`API request failed: ${response.status} ${response.statusText}`);
  }
  
  return response.json();
};

/**
 * Fetch all insight categories
 */
export const fetchInsightCategories = async (): Promise<InsightCategory[]> => {
  try {
    const response = await fetchWithAuth<InsightCategory[]>('/insights/categories');
    if (response.success && response.data) {
      return response.data;
    }
    throw new Error(response.error || 'Failed to fetch insight categories');
  } catch (error) {
    console.error('Error fetching insight categories:', error);
    throw error;
  }
};

/**
 * Fetch all insights across all categories
 */
export const fetchAllInsights = async (): Promise<InsightCategoryData> => {
  try {
    const response = await fetchWithAuth<InsightCategoryData>('/insights/all');
    if (response.success && response.data) {
      return response.data;
    }
    throw new Error(response.error || 'Failed to fetch all insights');
  } catch (error) {
    console.error('Error fetching all insights:', error);
    throw error;
  }
};

/**
 * Fetch insights for a specific category
 */
export const fetchCategoryInsights = async (category: string): Promise<Insight[]> => {
  try {
    const response = await fetchWithAuth<Insight[]>(`/insights/${category}`);
    if (response.success && response.data) {
      return response.data;
    }
    throw new Error(response.error || `Failed to fetch ${category} insights`);
  } catch (error) {
    console.error(`Error fetching ${category} insights:`, error);
    throw error;
  }
};

/**
 * Fetch velocity insights
 */
export const fetchVelocityInsights = async (): Promise<Insight[]> => {
  return fetchCategoryInsights('velocity');
};

/**
 * Fetch risk insights
 */
export const fetchRiskInsights = async (): Promise<Insight[]> => {
  return fetchCategoryInsights('risk');
};

/**
 * Fetch correlation insights
 */
export const fetchCorrelationInsights = async (): Promise<Insight[]> => {
  return fetchCategoryInsights('correlation');
};

/**
 * Fetch comparative insights
 */
export const fetchComparativeInsights = async (): Promise<Insight[]> => {
  return fetchCategoryInsights('comparative');
};
